import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * 每日報表表格組件
 * @param {Object} props 
 * @param {Array} props.data - 報表數據
 * @param {boolean} props.loading - 是否正在加載
 * @param {string} props.period - 時間週期 (daily, weekly, monthly)
 */
const DailyReportTable = ({ data = [], loading = false, period = 'daily' }) => {
    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');
    const [expandedRows, setExpandedRows] = useState({});

    // 格式化日期顯示
    const formatDate = (dateString) => {
        const date = new Date(dateString);

        switch (period) {
            case 'weekly':
                // 顯示週區間，例如：2023/01/01 - 2023/01/07
                const endOfWeek = new Date(date);
                endOfWeek.setDate(date.getDate() + 6);
                return `${date.toLocaleDateString('zh-TW')} - ${endOfWeek.toLocaleDateString('zh-TW')}`;

            case 'monthly':
                // 顯示月份，例如：2023年1月
                return date.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long' });

            case 'daily':
            default:
                // 顯示日期，例如：2023/01/01
                return date.toLocaleDateString('zh-TW');
        }
    };

    // 格式化數字顯示千分位
    const formatNumber = (number) => {
        return number?.toLocaleString() || '0';
    };

    // 格式化金額
    const formatCurrency = (amount) => {
        return `$${amount?.toLocaleString() || '0'}`;
    };

    // 處理排序點擊
    const handleSortClick = (field) => {
        if (field === sortField) {
            // 切換排序方向
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // 設定新的排序字段
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // 渲染排序圖標
    const renderSortIcon = (field) => {
        if (field !== sortField) {
            return null;
        }

        return sortDirection === 'asc'
            ? <ChevronUp className="w-4 h-4" />
            : <ChevronDown className="w-4 h-4" />;
    };

    // 切換行展開狀態
    const toggleRowExpanded = (rowId) => {
        setExpandedRows(prev => ({
            ...prev,
            [rowId]: !prev[rowId]
        }));
    };

    // 渲染同比變化
    const renderChange = (value) => {
        if (!value && value !== 0) return null;

        const isPositive = value > 0;
        const Icon = isPositive ? TrendingUp : TrendingDown;
        const colorClass = isPositive ? 'text-green-500' : 'text-red-500';

        return (
            <div className={`flex items-center ${colorClass}`}>
                <Icon className="w-4 h-4 mr-1" />
                <span>{isPositive ? '+' : ''}{value}%</span>
            </div>
        );
    };

    // 排序數據
    const sortedData = [...data].sort((a, b) => {
        let fieldA = a[sortField];
        let fieldB = b[sortField];

        // 處理日期類型
        if (sortField === 'date') {
            fieldA = new Date(fieldA).getTime();
            fieldB = new Date(fieldB).getTime();
        }

        // 處理數字類型
        if (typeof fieldA === 'number' && typeof fieldB === 'number') {
            return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
        }

        // 處理字符串類型
        if (typeof fieldA === 'string' && typeof fieldB === 'string') {
            return sortDirection === 'asc'
                ? fieldA.localeCompare(fieldB)
                : fieldB.localeCompare(fieldA);
        }

        return 0;
    });

    // 表頭配置
    const headers = [
        { field: 'date', label: '日期' },
        { field: 'total_orders', label: '訂單數' },
        { field: 'completed_orders', label: '完成訂單' },
        { field: 'cancellation_rate', label: '取消率' },
        { field: 'total_revenue', label: '總收入' },
        { field: 'average_fare', label: '平均車資' },
        { field: 'active_drivers', label: '活躍司機' }
    ];

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                展開
                            </th>
                            {headers.map(header => (
                                <th
                                    key={header.field}
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                    onClick={() => handleSortClick(header.field)}
                                >
                                    <div className="flex items-center">
                                        {header.label}
                                        {renderSortIcon(header.field)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sortedData.length === 0 ? (
                            <tr>
                                <td colSpan={headers.length + 1} className="px-6 py-4 text-center text-gray-500">
                                    無數據
                                </td>
                            </tr>
                        ) : (
                            sortedData.map((row, index) => (
                                <React.Fragment key={row.id || index}>
                                    <tr className={expandedRows[row.id || index] ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                                        <td className="px-2 py-4 whitespace-nowrap">
                                            <button
                                                onClick={() => toggleRowExpanded(row.id || index)}
                                                className="p-1 rounded-full hover:bg-gray-200"
                                            >
                                                {expandedRows[row.id || index] ? (
                                                    <ChevronDown className="w-5 h-5 text-gray-500" />
                                                ) : (
                                                    <ArrowRight className="w-5 h-5 text-gray-500" />
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {formatDate(row.date)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="font-medium">{formatNumber(row.total_orders)}</div>
                                            {renderChange(row.orders_growth)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="font-medium">{formatNumber(row.completed_orders)}</div>
                                            <div className="text-xs text-gray-400">
                                                ({Math.round((row.completed_orders / row.total_orders) * 100) || 0}%)
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className={`font-medium ${row.cancellation_rate > 15 ? 'text-red-500' :
                                                    row.cancellation_rate > 10 ? 'text-yellow-500' : 'text-gray-900'
                                                }`}>
                                                {row.cancellation_rate}%
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="font-medium">{formatCurrency(row.total_revenue)}</div>
                                            {renderChange(row.revenue_growth)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="font-medium">{formatCurrency(row.average_fare)}</div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="font-medium">{formatNumber(row.active_drivers)}</div>
                                            <div className="text-xs text-gray-400">
                                                ({Math.round((row.active_drivers / row.total_drivers) * 100) || 0}%)
                                            </div>
                                        </td>
                                    </tr>

                                    {/* 展開的詳細資訊行 */}
                                    {expandedRows[row.id || index] && (
                                        <tr>
                                            <td colSpan={headers.length + 1} className="px-4 py-4 bg-blue-50">
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div>
                                                        <h4 className="text-sm font-medium mb-2">訂單統計</h4>
                                                        <div className="space-y-1 text-sm">
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">總訂單:</span>
                                                                <span className="font-medium">{formatNumber(row.total_orders)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">完成訂單:</span>
                                                                <span className="font-medium">{formatNumber(row.completed_orders)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">取消訂單:</span>
                                                                <span className="font-medium">{formatNumber(row.cancelled_orders)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">平均接單時間:</span>
                                                                <span className="font-medium">{row.average_accept_time || 0} 秒</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">平均等待時間:</span>
                                                                <span className="font-medium">{row.average_wait_time || 0} 分鐘</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <h4 className="text-sm font-medium mb-2">收入統計</h4>
                                                        <div className="space-y-1 text-sm">
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">總收入:</span>
                                                                <span className="font-medium">{formatCurrency(row.total_revenue)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">平台收入:</span>
                                                                <span className="font-medium">{formatCurrency(row.platform_revenue)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">司機收入:</span>
                                                                <span className="font-medium">{formatCurrency(row.driver_revenue)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">平均車資:</span>
                                                                <span className="font-medium">{formatCurrency(row.average_fare)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">平均距離:</span>
                                                                <span className="font-medium">{row.average_distance || 0} 公里</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <h4 className="text-sm font-medium mb-2">司機統計</h4>
                                                        <div className="space-y-1 text-sm">
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">總司機數:</span>
                                                                <span className="font-medium">{formatNumber(row.total_drivers)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">活躍司機:</span>
                                                                <span className="font-medium">{formatNumber(row.active_drivers)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">平均接單數:</span>
                                                                <span className="font-medium">{row.average_trips_per_driver?.toFixed(1) || 0}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">平均在線時數:</span>
                                                                <span className="font-medium">{row.average_online_hours?.toFixed(1) || 0} 小時</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">平均評分:</span>
                                                                <span className="font-medium">{row.average_rating?.toFixed(1) || 0}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DailyReportTable;